export const SORT_ASC = 'asc';
export const SORT_DESC = 'desc';
export const DEFAULT_PN_SORT_FIELD = 'partNumber';
export const DRIVESHAFT_CARRIERS_TYPE = 'Driveshaft Carriers';

export function isDefaultPartNumberSort(sortField) {
    return sortField == null || sortField === DEFAULT_PN_SORT_FIELD;
}

function compareStringField(a, b) {
    if (a !== b) {
        if (a < b) return -1;
        if (a > b) return 1;
    }
    return 0;
}

function compareTertiaryType(aTertiary, bTertiary) {
    const a = aTertiary || '';
    const b = bTertiary || '';
    if (a !== b) {
        if (a === '' && b !== '') return -1;
        if (a !== '' && b === '') return 1;
        return compareStringField(a, b);
    }
    return 0;
}

function compareRevisionDescending(aRevision, bRevision) {
    if (aRevision !== bRevision) {
        if (aRevision > bRevision) return -1;
        if (aRevision < bRevision) return 1;
    }
    return 0;
}

function compareDriveshaftCarrierPartExpanded(a, b) {
    let result = compareStringField(a.part.manufacturerFor, b.part.manufacturerFor);
    if (result !== 0) return result;

    result = compareTertiaryType(a.part.tertiaryType, b.part.tertiaryType);
    if (result !== 0) return result;

    result = compareStringField(a.part.partId, b.part.partId);
    if (result !== 0) return result;

    return compareRevisionDescending(a.part.revision, b.part.revision);
}

function compareOtherPartExpanded(a, b) {
    let result = compareStringField(a.part.partType, b.part.partType);
    if (result !== 0) return result;

    result = compareStringField(a.part.manufacturerFor, b.part.manufacturerFor);
    if (result !== 0) return result;

    result = compareTertiaryType(a.part.tertiaryType, b.part.tertiaryType);
    if (result !== 0) return result;

    result = compareStringField(a.part.partId, b.part.partId);
    if (result !== 0) return result;

    return compareRevisionDescending(a.part.revision, b.part.revision);
}

/** Default parts table order: Driveshaft Carriers first, then category hierarchy. */
export function sortPartsExpandedDefault(items, direction = SORT_ASC) {
    if (!items?.length) return items ?? [];

    const driveshaftCarriers = items
        .filter((item) => item.part?.partType === DRIVESHAFT_CARRIERS_TYPE)
        .sort(compareDriveshaftCarrierPartExpanded);
    const otherParts = items
        .filter((item) => item.part?.partType !== DRIVESHAFT_CARRIERS_TYPE)
        .sort(compareOtherPartExpanded);
    const sorted = driveshaftCarriers.concat(otherParts);
    return direction === SORT_DESC ? [...sorted].reverse() : sorted;
}

function compareDriveshaftCarrierAssemblyExpanded(a, b) {
    let result = compareStringField(a.assembly.manufacturerFor, b.assembly.manufacturerFor);
    if (result !== 0) return result;

    result = compareTertiaryType(a.assembly.tertiaryType, b.assembly.tertiaryType);
    if (result !== 0) return result;

    result = compareStringField(a.assembly.assemblyId, b.assembly.assemblyId);
    if (result !== 0) return result;

    return compareRevisionDescending(a.assembly.revision, b.assembly.revision);
}

function compareOtherAssemblyExpanded(a, b) {
    let result = compareStringField(a.assembly.assemblyType, b.assembly.assemblyType);
    if (result !== 0) return result;

    result = compareStringField(a.assembly.manufacturerFor, b.assembly.manufacturerFor);
    if (result !== 0) return result;

    result = compareTertiaryType(a.assembly.tertiaryType, b.assembly.tertiaryType);
    if (result !== 0) return result;

    result = compareStringField(a.assembly.assemblyId, b.assembly.assemblyId);
    if (result !== 0) return result;

    return compareRevisionDescending(a.assembly.revision, b.assembly.revision);
}

/** Default assemblies table order: Driveshaft Carriers first, then category hierarchy. */
export function sortAssembliesExpandedDefault(items, direction = SORT_ASC) {
    if (!items?.length) return items ?? [];

    const driveshaftCarriers = items
        .filter((item) => item.assembly?.assemblyType === DRIVESHAFT_CARRIERS_TYPE)
        .sort(compareDriveshaftCarrierAssemblyExpanded);
    const otherAssemblies = items
        .filter((item) => item.assembly?.assemblyType !== DRIVESHAFT_CARRIERS_TYPE)
        .sort(compareOtherAssemblyExpanded);
    const sorted = driveshaftCarriers.concat(otherAssemblies);
    return direction === SORT_DESC ? [...sorted].reverse() : sorted;
}

function compareSubAssemblyExpanded(a, b) {
    let result = compareStringField(a.subAssembly.assemblyType, b.subAssembly.assemblyType);
    if (result !== 0) return result;

    result = compareStringField(a.subAssembly.manufacturerFor, b.subAssembly.manufacturerFor);
    if (result !== 0) return result;

    result = compareTertiaryType(a.subAssembly.tertiaryType, b.subAssembly.tertiaryType);
    if (result !== 0) return result;

    return compareStringField(a.subAssembly.subAssemblyId, b.subAssembly.subAssemblyId);
}

/** Default subassemblies table order by category hierarchy. */
export function sortSubAssembliesExpandedDefault(items, direction = SORT_ASC) {
    if (!items?.length) return items ?? [];

    const sorted = [...items].sort(compareSubAssemblyExpanded);
    return direction === SORT_DESC ? [...sorted].reverse() : sorted;
}

/** Default mixed assemblies/subassemblies list order used on the Assemblies page. */
export function sortAssemblyCatalogEntriesDefault(items, direction = SORT_ASC) {
    if (!items?.length) return items ?? [];

    const assemblies = sortAssembliesExpandedDefault(items.filter((item) => item.assembly), SORT_ASC);
    const subAssemblies = sortSubAssembliesExpandedDefault(items.filter((item) => item.subAssembly), SORT_ASC);
    const sorted = assemblies.concat(subAssemblies);
    return direction === SORT_DESC ? [...sorted].reverse() : sorted;
}

function getDefaultCatalogSortDirection(sortField, sortDirection) {
    return sortField == null ? SORT_ASC : sortDirection;
}

/** Apply display sort for catalog tables; PN + Description keeps category/default ordering. */
export function applyCatalogDisplaySort(items, sortField, sortDirection, options = {}) {
    const { kind = 'parts', allParts = [] } = options;
    const defaultDirection = getDefaultCatalogSortDirection(sortField, sortDirection);

    if (isDefaultPartNumberSort(sortField)) {
        if (kind === 'parts') {
            return sortPartsExpandedDefault(items, defaultDirection);
        }
        if (kind === 'assemblyCatalog') {
            return sortAssemblyCatalogEntriesDefault(items, defaultDirection);
        }
        if (kind === 'assemblies') {
            return sortAssembliesExpandedDefault(items, defaultDirection);
        }
        if (kind === 'subAssemblies') {
            return sortSubAssembliesExpandedDefault(items, defaultDirection);
        }
    }

    const accessors = kind === 'parts'
        ? createPartExpandedSortAccessors(allParts)
        : createAssemblyExpandedSortAccessors(items);
    return sortCollection(items, sortField, sortDirection, accessors);
}

export function usesCatalogCategoryHeaders(sortField) {
    return isDefaultPartNumberSort(sortField);
}

/**
 * Toggle or set sort state when a column header is clicked.
 */
export function getNextSortState(currentField, currentDirection, clickedField) {
    if (currentField === clickedField) {
        return {
            field: clickedField,
            direction: currentDirection === SORT_ASC ? SORT_DESC : SORT_ASC,
        };
    }
    return { field: clickedField, direction: SORT_ASC };
}

/**
 * Generic comparator for table column values (strings, numbers, dates).
 */
export function compareSortValues(a, b) {
    if (a == null && b == null) return 0;
    if (a == null || a === '') return 1;
    if (b == null || b === '') return -1;

    if (a instanceof Date && b instanceof Date) {
        return a.getTime() - b.getTime();
    }

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    const aStr = String(a).trim();
    const bStr = String(b).trim();
    const aNum = Number(aStr);
    const bNum = Number(bStr);
    if (aStr !== '' && bStr !== '' && !Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return aNum - bNum;
    }

    return aStr.localeCompare(bStr, undefined, { sensitivity: 'base', numeric: true });
}

/**
 * Sort a collection using a field key and optional per-field accessor map.
 */
export function sortCollection(items, sortField, sortDirection, accessors = {}) {
    if (!sortField || !items?.length) {
        return items ?? [];
    }

    const getValue = accessors[sortField] || ((item) => item?.[sortField]);
    const sorted = [...items].sort((a, b) => compareSortValues(getValue(a), getValue(b)));
    return sortDirection === SORT_DESC ? sorted.reverse() : sorted;
}

/** Text sort indicator used across admin tables. */
export function getSortIndicator(sortField, field, sortDirection) {
    if (sortField !== field) return '';
    return sortDirection === SORT_ASC ? ' ↑' : ' ↓';
}

/** Shared accessors for receiving-actions tables. */
export const RECEIVING_ACTIONS_SORT_ACCESSORS = {
    receivedDate: (action) => new Date(action.receivedDate || action.dateCreated || 0).getTime(),
    partNumber: (action) => `${action.partNumber || ''} ${action.partRevision || ''}`.trim().toLowerCase(),
    quantityReceived: (action) => Number(action.quantityReceived) || 0,
    userName: (action) => (action.userName || '').toLowerCase(),
    notes: (action) => (action.notes || '').toLowerCase(),
    packingSlip: (action) => (action.packingSlip?.name || action.packingSlipName || '').toLowerCase(),
};

/**
 * Build a handleSort handler for class components.
 * @param {object} component - React class component instance (this)
 * @param {object} options
 * @param {string} [options.fieldKey='sortField']
 * @param {string} [options.directionKey='sortDirection']
 * @param {boolean} [options.resetPage=false]
 * @param {Function} [options.onAfterSort]
 */
export function bindTableSortHandler(component, options = {}) {
    const fieldKey = options.fieldKey || 'sortField';
    const directionKey = options.directionKey || 'sortDirection';
    const resetPage = options.resetPage === true;
    const onAfterSort = options.onAfterSort;

    return (field) => {
        component.setState((prevState) => {
            const next = getNextSortState(prevState[fieldKey], prevState[directionKey], field);
            const newState = {
                [fieldKey]: next.field,
                [directionKey]: next.direction,
            };
            if (resetPage && prevState.page !== undefined) {
                newState.page = 0;
            }
            return newState;
        }, onAfterSort);
    };
}

export const USER_SORT_ACCESSORS = {
    name: (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase(),
    email: (user) => (user.email || '').toLowerCase(),
    permissions: (user) => (user.userPermissions || [])
        .map((p) => (typeof p === 'string' ? p : p.permissionType))
        .join(',')
        .toLowerCase(),
};

export const CATEGORY_SORT_ACCESSORS = {
    description: (category) => (category.description || '').toLowerCase(),
    slug: (category) => (category.slug || '').toLowerCase(),
    isVisible: (category) => (category.isVisible ? 1 : 0),
};

export const INVENTORY_LOG_SORT_ACCESSORS = {
    logDate: (log) => new Date(log.logDate || log.dateCreated || 0).getTime(),
    user: (log, context) => {
        const adminUser = context?.adminUsers?.find((x) => x.id === log.userId);
        return `${adminUser?.firstName || ''} ${adminUser?.lastName || ''}`.trim().toLowerCase();
    },
    quantity: (log) => Number(log.quantity) || 0,
    reason: (log) => (log.reason || '').toLowerCase(),
    partNumber: (log, context) => {
        const part = context?.parts?.find((x) => x.part.id === log.partId);
        return part ? `${part.part.partId} ${part.part.description || ''}`.toLowerCase() : '';
    },
};

export function sortCollectionWithContext(items, sortField, sortDirection, accessors = {}, context = {}) {
    if (!sortField || !items?.length) {
        return items ?? [];
    }

    const accessor = accessors[sortField];
    if (!accessor) {
        return items ?? [];
    }

    const sorted = [...items].sort((a, b) => compareSortValues(accessor(a, context), accessor(b, context)));
    return sortDirection === SORT_DESC ? sorted.reverse() : sorted;
}

export function createPartExpandedSortAccessors(allParts = []) {
    const findPartLabel = (id) => {
        if (!id) return '';
        const match = allParts.find((x) => x.part.id === id);
        return match ? `${match.part.partId} rev ${match.part.revision}` : '';
    };

    return {
        partNumber: (p) => `${p.part?.partId || ''} ${p.part?.description || ''}`.toLowerCase(),
        unitCost: (p) => Number(p.part?.unitCost) || 0,
        leadTime: (p) => Number(p.part?.leadTimeInWeeks) || 0,
        supersedes: (p) => findPartLabel(p.part?.supersedesId).toLowerCase(),
        supersededBy: (p) => {
            const superseding = allParts.find((x) => x.part.supersedesId === p.part?.id);
            return superseding ? `${superseding.part.partId}`.toLowerCase() : '';
        },
        totalReceived: (p) => Number(p.totalReceived) || 0,
        totalSold: (p) => Number(p.totalSold) || 0,
        totalUnpackaged: (p) => Number(p.totalUnpackaged) || 0,
        totalInFG: (p) => Number(p.totalInFG) || 0,
        totalPackaged: (p) => Number(p.totalPackaged) || 0,
        totalSoldUnpackaged: (p) => Number(p.totalSoldUnpackaged) || 0,
        fourWeekSales: (p) => Number(p.fourWeekAvgSales) || 0,
        twentyFourWeekSales: (p) => Number(p.twentyFourWeekAvgSales) || 0,
        avgSales: (p) => Number(p.averageSalesPerWeek) || 0,
        costType: (p) => (p.part?.costType || '').toLowerCase(),
    };
}

export function createAssemblyExpandedSortAccessors(allEntries = []) {
    const getItem = (entry) => entry.assembly || entry.subAssembly;
    const getId = (entry) => getItem(entry)?.assemblyId || getItem(entry)?.subAssemblyId || '';
    const getRevision = (entry) => getItem(entry)?.revision || '';
    const getDescription = (entry) => getItem(entry)?.description || '';

    return {
        partNumber: (entry) => `${getId(entry)} ${getDescription(entry)}`.toLowerCase(),
        shippingCost: (entry) => Number(getItem(entry)?.shippingCost) || 0,
        msrp: (entry) => Number(getItem(entry)?.msrp) || 0,
        fourWeekSales: (entry) => Number(entry.fourWeekSalesPerWeekWithRev ?? entry.fourWeekAvgSales) || 0,
        twentyFourWeekSales: (entry) => Number(entry.twentyFourWeekSalesPerWeekWithRev ?? entry.twentyFourWeekAvgSales) || 0,
        avgSales: (entry) => {
            const four = Number(entry.fourWeekSalesPerWeekWithRev ?? entry.fourWeekAvgSales) || 0;
            const twentyFour = Number(entry.twentyFourWeekSalesPerWeekWithRev ?? entry.twentyFourWeekAvgSales) || 0;
            return (four + twentyFour) / 2;
        },
        totalAssembledOnHand: (entry) => Number(entry.totalAssembledOnHandWithRev ?? entry.totalAssembledOnHand) || 0,
        assemblyType: (entry) => (getItem(entry)?.assemblyType || '').toLowerCase(),
    };
}

export function createInventoryWorksheetSortAccessors(allParts = [], vendors = []) {
    const getVendorName = (vendorId) => {
        const vendor = vendors.find((v) => v.id === vendorId);
        return (vendor?.name || '').toLowerCase();
    };

    return {
        partNumber: (p) => `${p.part?.partId || ''} ${p.part?.description || ''}`.toLowerCase(),
        vendor: (p) => getVendorName(p.part?.selectedVendorId),
        inventoryStrategy: (p) => (p.part?.inventoryStrategy || '').toLowerCase(),
        fourWeekSales: (p) => Number(p.fourWeekAvgSales) || 0,
        twentyFourWeekSales: (p) => Number(p.twentyFourWeekAvgSales) || 0,
        totalReceived: (p) => Number(p.totalReceived) || 0,
        totalSold: (p) => Number(p.totalSold) || 0,
        totalUnpackaged: (p) => Number(p.totalUnpackaged) || 0,
        totalInFG: (p) => Number(p.totalInFG) || 0,
        onOrder: (p) => Number(p.onOrder) || 0,
        leadTime: (p) => Number(p.part?.leadTimeInWeeks) || 0,
    };
}
